import React, { useState } from 'react';
import {
  detectGenderEnding,
  detectPluralEnding,
  normalizeGender,
  describePluralPattern,
  articleForGender,
} from '../utils/nounEndingPatterns';
import { GENUS_COLORS } from '../utils/genusColors';

/**
 * Renders a German noun with its classical gender or plural ending underlined
 * in the gender's color. Falls back to the plain word when no recognizable
 * pattern applies.
 *
 * Modes:
 *   - gender (default): underlines suffixes like -ung/-chen/-ling when they
 *     agree with the noun's known gender. Strong rules (e.g. -ung, -chen)
 *     use a solid underline, medium rules (e.g. -er, -um) use a dashed
 *     underline. On hover, a tooltip shows the genus and plural rule.
 *   - plural: underlines the suffix that was added to `singular` to form the
 *     displayed plural word (umlaut shifts are not highlighted).
 *
 * `neutral` renders the gender-mode underline in ink instead of the gender
 * color and suppresses the tooltip — for question prompts, where the
 * colored underline would hand over the answer before retrieval.
 */
const NounWithEnding = ({
  word,
  gender,
  singular,
  mode = singular ? 'plural' : 'gender',
  neutral = false,
  className = '',
  style,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  if (!word || typeof word !== 'string') return null;

  let suffixLength = 0;
  let match = null;
  const genderKey = normalizeGender(gender);
  const color = genderKey ? GENUS_COLORS[genderKey]?.hex : null;

  if (mode === 'plural' && singular) {
    const plSuffix = detectPluralEnding(singular, word);
    if (plSuffix) suffixLength = plSuffix.length;
  } else if (mode === 'gender') {
    match = detectGenderEnding(word, gender);
    if (match) suffixLength = match.suffix.length;
  }

  if (suffixLength === 0 || suffixLength >= word.length) {
    return <span className={className} style={style}>{word}</span>;
  }

  const stem = word.slice(0, -suffixLength);
  const ending = word.slice(-suffixLength);

  const isMedium = mode === 'gender' && match?.reliability === 'medium';
  const endingStyle = {
    textDecoration: isMedium ? 'underline dashed' : 'underline',
    textDecorationColor: neutral ? 'var(--ink-500)' : (color || 'currentColor'),
    textDecorationThickness: '2px',
    textUnderlineOffset: '3px',
    cursor: !neutral && mode === 'gender' && match ? 'help' : undefined,
  };

  const canTooltip = !neutral && mode === 'gender' && match;
  const tooltipGender = match?.gender;
  const tooltipArticle = articleForGender(tooltipGender);
  const pluralLabel = describePluralPattern(match?.plural);
  const reliabilityLabel = match?.reliability === 'strong'
    ? 'Immer'
    : 'Meistens — mit Ausnahmen';

  return (
    <span className={className} style={{ ...style, position: 'relative', display: 'inline-block' }}>
      {stem}
      <span
        style={endingStyle}
        onMouseEnter={canTooltip ? () => setShowTooltip(true) : undefined}
        onMouseLeave={canTooltip ? () => setShowTooltip(false) : undefined}
      >
        {ending}
      </span>
      {canTooltip && showTooltip && (
        <span
          role="tooltip"
          className="absolute bottom-full left-1/2 mb-2 px-3 py-2 bg-[var(--ink-900)] text-white text-xs whitespace-nowrap z-20"
          style={{ transform: 'translateX(-50%)' }}
        >
          <span className="block font-semibold">-{match.suffix.toLowerCase()}</span>
          <span className="block mt-1">
            {tooltipArticle ? `${tooltipArticle} (${tooltipGender})` : tooltipGender}
          </span>
          {pluralLabel && (
            <span className="block">Plural: {pluralLabel}</span>
          )}
          <span className="block mt-1 opacity-80">{reliabilityLabel}</span>
          <span
            className="absolute top-full left-1/2 w-0 h-0"
            style={{
              transform: 'translateX(-50%)',
              borderLeft: '4px solid transparent',
              borderRight: '4px solid transparent',
              borderTop: '4px solid var(--ink-900)',
            }}
          />
        </span>
      )}
    </span>
  );
};

export default NounWithEnding;
